import { Injectable } from '@angular/core';
import { toCanvas } from 'html-to-image';
import jsPDF from 'jspdf';

@Injectable({ providedIn: 'root' })
export class PdfService {
  constructor() {}

  async downloadElementAsPdf(root: HTMLElement | null, filename = 'report', button?: HTMLElement, opts?: { scale?: number; pdfFactory?: (canvas: HTMLCanvasElement) => any; beforeSave?: (pdf: any, canvas: HTMLCanvasElement) => void }) {
    if (!root) return;

    // Manage button loader by toggling a CSS class (.loading) and disabled state.
    // This preserves button innerHTML and allows component-scoped spinners to show/hide.
    let prevDisabled = false;
    const btn = button as HTMLElement | undefined;
    if (btn) {
      try {
        prevDisabled = !!(btn as HTMLButtonElement).disabled;
      } catch {
        prevDisabled = false;
      }
      try {
        // add loading class and disable
        btn.classList.add('loading');
        try { (btn as HTMLButtonElement).disabled = true; } catch {}
      } catch {}
    }

    // hide elements marked for exclusion
    const hidden: HTMLElement[] = [];
    root.querySelectorAll('[data-pdf-hide]').forEach((el) => {
      const e = el as HTMLElement;
      hidden.push(e);
      e.setAttribute('data-prev-display', e.style.display || '');
      e.style.display = 'none';
    });

    const restoreVideos = await this.snapshotVideos(root);

    try {
      await this.waitForImages(root);
      await this.waitForFonts();

      const canvas = await toCanvas(root, {
        pixelRatio: opts?.scale ?? 2,
        cacheBust: true,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'pt', 'a4');

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (imgHeight <= pageHeight) {
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, '', 'FAST');
      } else {
        let y = 0;
        let remaining = imgHeight;
        pdf.addImage(imgData, 'PNG', 0, y, imgWidth, imgHeight, '', 'FAST');
        remaining -= pageHeight;
        while (remaining > -pageHeight) {
          pdf.addPage();
          y -= pageHeight;
          pdf.addImage(imgData, 'PNG', 0, y, imgWidth, imgHeight, '', 'FAST');
          remaining -= pageHeight;
        }
      }

      pdf.save(this.sanitizeFileName(filename) + '.pdf');
    } finally {
      try { restoreVideos(); } catch {}
      hidden.forEach((e) => {
        e.style.display = e.getAttribute('data-prev-display') || '';
        e.removeAttribute('data-prev-display');
      });

      if (btn) {
        try {
          btn.classList.remove('loading');
        } catch {}
        try { (btn as HTMLButtonElement).disabled = prevDisabled; } catch {}
      }
    }
  }

  private async waitForImages(root: HTMLElement) {
    const imgs = Array.from(root.querySelectorAll('img'));
    await Promise.all(
      imgs.map((img) => {
        if ((img as HTMLImageElement).complete) return Promise.resolve();
        return new Promise<void>((res) => {
          const done = () => res();
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        });
      })
    );
  }

  private async waitForFonts() {
    if ((document as any).fonts && (document as any).fonts.status !== 'loaded') {
      try { await (document as any).fonts.ready; } catch { }
    }
  }

  private sanitizeFileName(name: string) {
    return name.trim().replace(/[^a-z0-9\-_]+/gi, '_').slice(0, 80);
  }

  private async snapshotVideos(root: HTMLElement) {
    const videos = Array.from(root.querySelectorAll('video')) as HTMLVideoElement[];
    const restores: Array<() => void> = [];

    if (videos.length === 0) return () => {};

    await Promise.all(videos.map(v => {
      if ((v as HTMLVideoElement).readyState >= 1) return Promise.resolve();
      return new Promise<void>(res => {
        const onLoaded = () => { v.removeEventListener('loadedmetadata', onLoaded); res(); };
        v.addEventListener('loadedmetadata', onLoaded, { once: true });
        setTimeout(() => res(), 2000);
      });
    }));

    for (const v of videos) {
      try {
        const w = v.videoWidth || v.clientWidth || 320;
        const h = v.videoHeight || v.clientHeight || 180;
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const ctx = c.getContext('2d');

        let dataUrl: string | null = null;
        if (ctx) {
          try {
            ctx.drawImage(v, 0, 0, w, h);
            dataUrl = c.toDataURL('image/png');
          } catch {
            if (v.poster) dataUrl = v.poster;
          }
        }

        if (!dataUrl) {
          const prevDisplay = v.style.display || '';
          v.style.display = 'none';
          restores.push(() => { v.style.display = prevDisplay; });
          continue;
        }

        const img = document.createElement('img');
        img.src = dataUrl;
        img.alt = 'video frame';
        img.style.width = `${v.clientWidth || w}px`;
        img.style.height = `${v.clientHeight || h}px`;
        img.style.objectFit = (getComputedStyle(v).objectFit as any) || 'cover';

        const vStyle = getComputedStyle(v);
        img.style.display = vStyle.display === 'inline' ? 'inline-block' : vStyle.display;
        img.style.borderRadius = vStyle.borderRadius;
        img.style.maxWidth = vStyle.maxWidth;
        img.style.maxHeight = vStyle.maxHeight;

        v.insertAdjacentElement('afterend', img);
        const prevDisplay = v.style.display || '';
        v.style.display = 'none';

        restores.push(() => {
          img.remove();
          v.style.display = prevDisplay;
        });
      } catch {
        const prevDisplay = v.style.display || '';
        v.style.display = 'none';
        restores.push(() => { v.style.display = prevDisplay; });
      }
    }

    return () => restores.forEach(fn => fn());
  }
}
